import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

/**
 * Reels Studio — the moderation room for the short-video feed.
 * Every reel is a verified YouTube pointer: adding one here sends only the
 * URL, and the engine confirms it against YouTube's oEmbed endpoint before
 * anything is stored. Titles and channels are YouTube's own words and are
 * deliberately not editable.
 */

const STATUS_META = {
  PENDING: { label: 'Pending', chip: 'bg-amber-100 text-amber-700' },
  APPROVED: { label: 'Approved', chip: 'bg-emerald-100 text-emerald-700' },
  FEATURED: { label: 'Featured', chip: 'bg-indigo-100 text-indigo-700' },
  REJECTED: { label: 'Rejected', chip: 'bg-rose-100 text-rose-600' }
};
const CATEGORIES = ['kiirtan', 'music', 'discourse', 'seva', 'yoga', 'sangha'];

const ReelsStudio = () => {
  const [reels, setReels] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ url: '', category: 'kiirtan', official: false });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const fetchReels = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (search.trim()) params.set('q', search.trim());
      const res = await api.get(`/reels/admin?${params.toString()}`);
      if (res.data.success) {
        setReels(res.data.data);
        setStats(res.data.stats || {});
      }
    } catch (err) {
      console.error(err);
      setNotice({ kind: 'error', text: 'Could not load reels.' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, search]);

  useEffect(() => { fetchReels(); }, [fetchReels]);

  const flash = (kind, text) => {
    setNotice({ kind, text });
    setTimeout(() => setNotice(null), 4000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/reels', form);
      setForm({ url: '', category: 'kiirtan', official: false });
      setAdding(false);
      flash('ok', 'Verified with YouTube and added as Approved.');
      fetchReels();
    } catch (err) {
      flash('error', err.response?.data?.message || 'YouTube could not verify this link.');
    } finally {
      setBusy(false);
    }
  };

  const patchReel = async (id, body, okText) => {
    try {
      await api.patch(`/reels/${id}`, body);
      if (okText) flash('ok', okText);
      fetchReels();
    } catch (err) {
      flash('error', err.response?.data?.message || 'Update failed.');
    }
  };

  const deleteReel = async (reel) => {
    if (!window.confirm(`Remove "${reel.title}" from the collection?`)) return;
    try {
      await api.delete(`/reels/${reel._id}`);
      flash('ok', 'Removed.');
      fetchReels();
    } catch (err) {
      flash('error', 'Delete failed.');
    }
  };

  const statCards = [
    { label: 'Total reels', value: stats.total ?? '—', icon: 'movie' },
    { label: 'Official channels', value: stats.official ?? '—', icon: 'verified' },
    { label: 'Pending review', value: stats.pending ?? 0, icon: 'pending_actions' },
    { label: 'Featured', value: stats.featured ?? 0, icon: 'star' },
    { label: 'Likes (all time)', value: stats.likes ?? 0, icon: 'favorite' },
    { label: 'Views (all time)', value: stats.views ?? 0, icon: 'visibility' }
  ];

  return (
    <div className="p-8">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-[#2A2858] font-serif">Reels Studio</h2>
          <p className="text-stone-500 mt-1">
            Moderate the short-video feed — every entry is verified against YouTube before it is stored
          </p>
        </div>
        <button
          onClick={() => setAdding(!adding)}
          className="bg-[#2A2858] text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-[#14122B] transition-all shadow-lg"
        >
          <span className="material-symbols-outlined">{adding ? 'close' : 'add'}</span>
          {adding ? 'Cancel' : 'Add Reel by URL'}
        </button>
      </div>

      {notice && (
        <div className={`mb-6 px-5 py-3 rounded-xl text-sm font-medium ${notice.kind === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-600 border border-rose-200'}`}>
          {notice.text}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 shadow-md border border-stone-100">
            <span className="material-symbols-outlined text-[#E8B84B]">{s.icon}</span>
            <div className="text-2xl font-bold text-[#2A2858] mt-2">{s.value}</div>
            <div className="text-[11px] text-stone-500 font-bold uppercase tracking-widest mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Add form */}
      {adding && (
        <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#E8B84B]/20 mb-10">
          <h3 className="text-xl font-bold text-[#2A2858] mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined">link</span> Add a verified reel
          </h3>
          <p className="text-stone-500 text-sm mb-6">
            Paste any YouTube link (watch, shorts or youtu.be). The engine asks YouTube to confirm the
            video exists and stores YouTube's own title and channel — nothing hand-typed.
          </p>
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div className="md:col-span-3">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">YouTube URL</label>
              <input
                type="url" required value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=…"
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-[#E8B84B] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-[#E8B84B] focus:outline-none capitalize"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 py-3 text-sm font-medium text-stone-600 cursor-pointer">
              <input
                type="checkbox" checked={form.official}
                onChange={(e) => setForm({ ...form, official: e.target.checked })}
                className="w-4 h-4 accent-[#2A2858]"
              />
              Official channel
            </label>
            <button
              type="submit" disabled={busy}
              className="bg-[#E8B84B] text-[#2A2858] px-6 py-3 rounded-xl font-bold hover:bg-[#d9a93c] transition-all disabled:opacity-50"
            >
              {busy ? 'Verifying…' : 'Verify & Add'}
            </button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {['', 'PENDING', 'APPROVED', 'FEATURED', 'REJECTED'].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${statusFilter === s ? 'bg-[#2A2858] text-white shadow-md' : 'bg-white text-stone-500 border border-stone-200 hover:border-[#2A2858]/40'}`}
          >
            {s ? STATUS_META[s].label : 'All'}
            {s === 'PENDING' && stats.pending ? ` (${stats.pending})` : ''}
          </button>
        ))}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 rounded-full text-sm font-bold bg-white text-stone-500 border border-stone-200 capitalize"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search titles…"
          className="px-4 py-2 rounded-full text-sm bg-white border border-stone-200 focus:border-[#E8B84B] focus:outline-none min-w-[200px]"
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-stone-400 py-20 text-center">Loading reels…</div>
      ) : reels.length === 0 ? (
        <div className="bg-white rounded-3xl p-16 text-center border border-stone-100">
          <span className="material-symbols-outlined text-stone-200 text-6xl">movie</span>
          <p className="text-stone-400 mt-3">No reels match this filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {reels.map((r) => (
            <div key={r._id} className="bg-white rounded-2xl shadow-md border border-stone-100 overflow-hidden flex flex-col">
              <a href={r.url} target="_blank" rel="noreferrer" className="relative block aspect-video bg-stone-100 group" title="Open on YouTube">
                {r.thumb && <img src={r.thumb} alt={r.title} className="w-full h-full object-cover" loading="lazy" />}
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                  <span className="material-symbols-outlined text-white text-5xl">play_circle</span>
                </span>
                <span className={`absolute top-2 left-2 px-2.5 py-1 rounded-full text-[11px] font-bold ${STATUS_META[r.status].chip}`}>
                  {STATUS_META[r.status].label}
                </span>
                {r.official && (
                  <span className="absolute top-2 right-2 px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#2A2858] text-[#E8B84B] flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">verified</span> Official
                  </span>
                )}
              </a>
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-bold text-[#2A2858] text-sm leading-snug line-clamp-2" title={r.title}>{r.title}</div>
                <div className="text-xs text-stone-400 mt-1">{r.channel}</div>
                <div className="flex items-center gap-3 text-xs text-stone-500 mt-2">
                  <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[14px]">favorite</span>{r.likes}</span>
                  <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[14px]">visibility</span>{r.views}</span>
                  <select
                    value={r.category}
                    onChange={(e) => patchReel(r._id, { category: e.target.value })}
                    className="ml-auto px-2 py-1 rounded-lg border border-stone-200 text-[11px] capitalize"
                    title="Category"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-stone-100">
                  {r.status !== 'APPROVED' && (
                    <button onClick={() => patchReel(r._id, { status: 'APPROVED' }, 'Approved.')} className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">Approve</button>
                  )}
                  {r.status !== 'FEATURED' && (
                    <button onClick={() => patchReel(r._id, { status: 'FEATURED' }, 'Featured — it now leads the feed.')} className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors">Feature</button>
                  )}
                  {r.status !== 'REJECTED' && (
                    <button onClick={() => patchReel(r._id, { status: 'REJECTED' }, 'Rejected — hidden from the feed.')} className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">Reject</button>
                  )}
                  <button onClick={() => deleteReel(r)} className="px-3 py-2 rounded-lg text-xs font-bold bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors" title="Delete permanently">
                    <span className="material-symbols-outlined text-[14px]">delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReelsStudio;
